import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { BookOpen, Flame, Heart, Sparkles, ShoppingBag, ArrowRight } from "lucide-react";
import LiveEventsDialog from "@/components/LiveEventsDialog";
import PostsFeed from "@/components/PostsFeed";

export default function Home() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const openLogin = () => navigate("/login");

  return (
    <div>
      <LiveEventsDialog />
      {/* Hero */}
      <section className="relative overflow-hidden hero-glow" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-12 gap-10 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[#B45309] mb-4">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.2em]">Est. Devotional Community</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#5C061C] leading-[1.05]">
              {t("brand")}
            </h1>
            <p className="mt-4 text-xl sm:text-2xl text-[#B45309] font-serif">
              {t("invocation")}
            </p>
            <p className="mt-5 text-lg text-[#6E6259] max-w-xl leading-relaxed">
              {t("tagline")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/events">
                <Button className="btn-saffron h-12 rounded-full px-6 text-base" data-testid="hero-cta-seva">
                  <Heart className="h-4 w-4 mr-2" /> {t("hero.cta1")}
                </Button>
              </Link>
              <Link to="/books">
                <Button variant="outline" className="h-12 rounded-full px-6 text-base border-[#5C061C] text-[#5C061C] hover:bg-[#F4EEDF]" data-testid="hero-cta-books">
                  <BookOpen className="h-4 w-4 mr-2" /> {t("hero.cta2")}
                </Button>
              </Link>
              {!user && (
                <Button onClick={openLogin} className="btn-maroon h-12 rounded-full px-6 text-base" data-testid="hero-cta-login">
                  <Flame className="h-4 w-4 mr-2" /> {t("login.login")}
                </Button>
              )}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-3">
              <div className="relative rounded-3xl overflow-hidden card-warm">
                <img src="https://customer-assets-agu9un31.emergentagent.net/job_spiritual-library-7/artifacts/lfvw71dv_pic1.jpg" alt="Sri Vidya Devi" className="w-full h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#5C061C]/50 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="font-serif text-lg drop-shadow">Devi Sri Vidya</p>
                  <p className="text-xs opacity-95">Divine mother</p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden card-warm">
                <img src="https://customer-assets-agu9un31.emergentagent.net/job_spiritual-library-7/artifacts/4qj0p37s_WhatsApp%20Image%202026-09-18%20at%201.30.22%20PM%20%281%29.jpeg" alt="Guruji" className="w-full h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#5C061C]/50 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="font-serif text-lg drop-shadow">Pujya Guruji</p>
                  <p className="text-xs opacity-95">Blessings of the guru</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-[#EAB308]/30 blur-2xl" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="divider-om max-w-md mx-auto mb-8"><span className="font-serif text-2xl">ॐ</span></div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: BookOpen, title: t("books.title"), body: t("books.subtitle"), to: "/books", testid: "pillar-books" },
            { icon: ShoppingBag, title: t("shop.title"), body: t("shop.subtitle"), to: "/shop", testid: "pillar-shop" },
            { icon: Heart, title: t("events.title"), body: t("events.subtitle"), to: "/events", testid: "pillar-events" },
          ].map((p) => (
            <Link key={p.to} to={p.to} data-testid={p.testid} className="card-warm rounded-2xl p-6 hover:-translate-y-1 transition-transform block">
              <div className="h-12 w-12 rounded-xl bg-[#F4EEDF] grid place-items-center text-[#5C061C] mb-4">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="font-serif text-2xl text-[#5C061C]">{p.title}</h3>
              <p className="mt-2 text-[#6E6259] leading-relaxed">{p.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[#D97706] font-semibold">Explore <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Daily posts / announcements */}
      <PostsFeed />

      {/* Guruji's message */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="guruji-message">
        <div className="card-warm rounded-3xl p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-[#EAB308]/20 blur-3xl" />
          <div className="inline-flex items-center gap-2 text-[#B45309] mb-3">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Guruji's Message</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#5C061C] leading-tight">
            मेरे प्रिय समस्त सनातन धर्मावलंबी बंधु-भगिनी, माता एवं मित्र एवं भविष्य की पीढ़ी
          </h2>
          <p className="mt-2 text-sm text-[#6E6259]">उनको मेरा मार्मिक संदेश।</p>
          <div className="mt-6 space-y-4 text-[#1F1A17] leading-relaxed">
            <p>प्राचीन काल में हमारी संस्कृति बहुत समृद्ध, सुदृढ़, सुव्यवस्थित, सुसंस्कारी रही है। और हमारे पास आगम निगम, वैदिक, नैतिक, आयुर्वेद, धनुर्वेद, गंधर्ववेद उपवेदों से भरी हुई आर्ष ग्रंथों की बड़ी श्रृंखला है। जिसका हम संसार के समस्त अन्य धर्मावलंबी या अन्य प्रदेशों, देशों, अन्य देशों की तुलना करें तो वह हजारों वर्ष तक पुस्तकें लिखेंगे तो भी हमारी जो संस्कृति से संबंधित परंपराओं से संबंधित पुस्तकों का तुलना करना उनके लिए संभव हुआ भी नहीं है और होगा भी नहीं।</p>
            <p>अपने आप को एक छोटे तृण मात्र की तरह जोड़कर एक आध्यात्मिक, सामाजिक, व्यावहारिक एवं लोकोपयोगी, लोकोन्मुख अनुभव देना हमारा प्रण है। और आने वाली पीढ़ियों को हमारी परंपरा से अवगत कराकर उनके अंदर में चैतन्य भरना और एक उत्तम समाज की रचना में उनको जोड़कर और उनको एक बृहद राजपथ की तरह मार्गदर्शन करना यह हमारा लक्ष्य है। जिसमें प्राचीन ऋषियों से लेकर विद्वान और धर्म सम्राटों के सहित राजऋषियों की तरह जिन्होंने अपना योगदान किया। इसमें माताएं, वीरांगनाएं, विदुषियां और भक्ति में डूबी हुई हमारी मातृत्व शक्ति का भी हमारे पास बहुत बड़ा धरोहर है, जिसे हम आने वाली पीढ़ियों को अवगत कराकर और इस संस्कृति को एक जीवित रखने के लिए हमारा दृढ़ संकल्प है।</p>
            <p>हमारी परंपराओं के पीछे एक बहुत बड़े वैज्ञानिक दृष्टिकोण है और भौगोलिक दृष्टिकोण है, प्राकृतिक दृष्टिकोण है, सामाजिक दृष्टिकोण है। इन सबका एक निचोड़ लाकर और हम आगे की आने वाली पीढ़ियों के लिए एक बहुत बड़ा उपादान खड़ा करके उनके लिए संशोधन केंद्र स्थापित करने का हमारा विचार है। अतएव दक्षिण भारत से लेकर उत्तर भारत, पूर्व भारत से लेकर पश्चिम भारत एवं भारत का जो भू-भाग अन्य देशों में विलय हो चुका है, उस समय के जो प्राचीन ग्रंथों में विवरण हैं, वह विवरणों का संकलन करके और पुनः धर्म जागृति की ओर अपना ध्यान लक्षित करके और एक बहुत बड़ा आध्यात्मिक आंदोलन खड़ा करके और <span className="font-serif text-[#5C061C] font-semibold">अहिंसा परमो धर्म</span> का क्या अर्थ वास्तविक है यह भी समझाना है।</p>
            <p><span className="font-serif text-[#5C061C] font-semibold">"धर्मो रक्षति रक्षितः"</span> यह शब्द को कैसे सार्थक करना और वर्तमान में हमारी तरुण शक्ति जो व्यर्थ के व्यसनों में, व्यसनों के गर्ता में डूबती जा रही है। जिन्हें अपना आने वाले समय का अस्तित्व क्या होगा यह उनको ध्यान में नहीं है। चरित्र से लेकर और जीवनयापन के जो मार्ग हैं, वह भी दूषित हो चुके हैं। उन्हें सुव्यवस्थित करने का एक बहुत बड़ा हमारा प्रयास है और इस प्रयास में सभी का सहयोग अनिवार्य है।</p>
            <p>हम छोटे से निहित स्वार्थ को त्याग कर और बड़े सामाजिक स्वार्थ की तरफ प्रेरित हों, जिसमें <span className="font-serif text-[#5C061C] font-semibold">सर्वे भवंतु सुखिनः सर्वे संतु निरामयाः सर्वे भद्राणि पश्यंतु मा कश्चित् दुःख माप्नुयात्</span>। यह हमारा वेदों का उद्घोष है, उसको सार्थक करने का प्रयत्न करें।</p>
            <p>इसमें आगे आओ हम पुनः संकल्प लें, प्रण लें, वेदों की तरफ लौटें, परंपराओं के सही आकलन को समझें। परंपराओं का सही से पालन करें। जो परंपराएं किसी भी प्रकार का भेद उत्पन्न न करती हों, लिंग भेद, जाति भेद से दूर हों और जो सर्वथा व्यापक दृष्टिकोण अपनाए हुए हैं, ऐसी परंपराओं को हम पुनः जागृत करें। <span className="font-serif text-[#5C061C] font-semibold">वसुधैव कुटुम्बकम्</span> यह जो विचारधारा है, <span className="font-serif text-[#5C061C] font-semibold">कृण्वन्तो विश्वमार्यम्</span> यह विचार को फिर से पुनर्जागृत करके पुनर्जीवित करें। यह अपने प्राणशक्ति को योग के माध्यम से केंद्रीभूत करके और सशक्त करें। हमारे धर्म वृक्ष की प्रत्येक पत्तों तक विचारों के दृष्टिकोण का जल प्राप्त हो और वह पल्लवित हों, यह हमारी कामना है। प्राचीन ग्रंथों का पुनरावलोकन और प्राचीन ग्रंथों का सिंहावलोकन करने का एक संकल्प करें।</p>
          </div>
          <div className="mt-6 pt-6 border-t border-[#E8DEC8] flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#F4EEDF] grid place-items-center text-[#5C061C] font-serif">ॐ</div>
            <div>
              <p className="font-serif text-lg text-[#5C061C]">Shree Swarup Guruji</p>
              <p className="text-xs uppercase tracking-widest text-[#B45309]">Dr. Manoj Diwakar Joshi</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sacred verse block */}
      <section className="max-w-4xl mx-auto px-4 py-14 text-center">
        <p className="font-serif text-3xl sm:text-4xl text-[#5C061C] leading-tight">
          "सर्वे भवन्तु सुखिनः। सर्वे सन्तु निरामयाः।"
        </p>
        <p className="mt-2 font-serif text-2xl sm:text-3xl text-[#5C061C]/90">"सर्वे भद्राणि पश्यन्तु। मा कश्चिद् दुःखभाग् भवेत्।"</p>
        <p className="mt-4 text-[#6E6259]">May all beings be happy. May all be free from illness. May all see auspiciousness. May none suffer.</p>
      </section>
    </div>
  );
}
